import React, { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  useAddClusterToStudentsMutation,
  useGetStudentsByClusterQuery,
  useRemoveClusterFromStudentsMutation,
  GetStudentsByCampusDocument,
} from "../../../../graphql/generated.tsx";
import { toast } from "../../../../toast";
import { BiImport } from "react-icons/bi";
import { MdRemoveCircleOutline, MdTableChart } from "react-icons/md";
import { FaDownload } from "react-icons/fa6";
import { Button, Modal, Table } from "flowbite-react";
import AdminLayout from "../../Layout/index.jsx";
import { AsyncPaginate } from "react-select-async-paginate";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../../components/Pagination/index.jsx";

const PAGE_SIZE = 15;
const FETCH_LIMIT = 500;

const StudentsTab = ({ campusId, clusterId, ClusterData, campusName }) => {
  const client = useApolloClient();
  const [page, setPage] = useState(1);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const toggleAddStudentModal = () =>
    setShowAddStudentModal(!showAddStudentModal);

  const loadOptions = async (inputValue, _, { page: currentPage }) => {
    const { data } = await client.query({
      query: GetStudentsByCampusDocument,
      variables: { campusId, page: currentPage, limit: 10, searchTerm: inputValue },
      fetchPolicy: "network-only",
    });
    const fetched = data?.getStudentsByCampus?.students || [];
    const total = data?.getStudentsByCampus?.total || 0;
    const loaded = (currentPage - 1) * 10 + fetched.length;
    return {
      options: fetched.map((s) => ({ label: s.email, value: s.id })),
      hasMore: loaded < total,
      additional: { page: currentPage + 1 },
    };
  };

  const [addClusterToStudent] = useAddClusterToStudentsMutation();
  const [removeClusterFromStudents] = useRemoveClusterFromStudentsMutation();

  const { data: studentsData, error: studentError, refetch: refetchStudents } =
    useGetStudentsByClusterQuery({
      variables: {
        clusterId,
        searchTerm: searchTerm || "",
        page: 1,
        limit: FETCH_LIMIT,
      },
    });

  const allStudents = studentsData?.getStudentsByCluster ?? [];

  const totalPages = Math.max(1, Math.ceil(allStudents.length / PAGE_SIZE));

  const students = useMemo(
    () =>
      allStudents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [allStudents, page]
  );

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(allStudents.length / PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [allStudents.length, page]);

  const handleSearch = (event) => setSearchTerm(event.target.value);
  const handlePageChange = (newPage) => setPage(newPage);

  const handleAddStudents = async (e) => {
    e.preventDefault();
    await addClusterToStudent({
      variables: {
        input: {
          clusterId,
          studentIds: selectedStudents.map((c) => c.value),
          courseIds: ClusterData?.getCluster.courseIds,
          campusName,
        },
      },
    });

    await refetchStudents();
    setShowAddStudentModal(false);
    setSelectedStudents([]);
    toast.success("Students added successfully");
  };

  const handleSelectStudent = (studentId) => {
    setSelectedStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleDeleteStudents = async (singleStudent) => {
    const studentIdsToDelete = selectedStudentIds.length
      ? selectedStudentIds
      : singleStudent;

    try {
      await removeClusterFromStudents({
        variables: {
          input: {
            clusterId,
            studentIds: studentIdsToDelete,
            courseIds: ClusterData?.getCluster.courseIds,
            campusName,
          },
        },
      });

      await refetchStudents();

      setSelectedStudentIds([]);
      toast.success("Students removed successfully");
    } catch (error) {
      console.error("Error removing students:", error);
    }
  };

  /** CSV columns limited to name, email, department only (same dropdown UI as LeetcodeReportDetails). */
  const downloadStudentsCsv = () => {
    if (!allStudents.length) {
      toast.info("No students to download.");
      return;
    }

    const escapeCsvValue = (value) => {
      const str = value ?? "";
      const normalized = String(str).replace(/"/g, '""');
      return `"${normalized}"`;
    };

    const headers = ["Name", "Email", "Department"];
    const rows = allStudents.map((s) => [
      s.name ?? "-",
      s.email ?? "-",
      s.department ?? "-",
    ]);

    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    const toSafeFilenamePart = (value, fallback) => String(value || fallback)
      .replace(/[\\/:*?"<>|]+/g, "-")
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || fallback;
    const safeCampusName = toSafeFilenamePart(campusName, "campus");
    const safeClusterName = toSafeFilenamePart(
      ClusterData?.getCluster?.name || clusterId,
      "cluster"
    );
    link.href = URL.createObjectURL(blob);
    link.download = `${safeCampusName}_${safeClusterName}_students.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (studentError)
    return <AdminLayout>Error: {studentError.message}</AdminLayout>;

  return (
    <>
      <div className="p-4">
        {/* Top Row */}
        <div className="flex justify-between items-center mb-5">
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by name or email"
            className="border border-gray-300 rounded px-3 py-1 w-1/3"
          />

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDownloadDropdown((prev) => !prev)}
                className="inline-flex items-center justify-center w-9 h-9 text-slate-600 transition-all hover:text-slate-900 focus:outline-none"
                title="Download"
                aria-label="Download"
              >
                <FaDownload size={18} />
              </button>

              {showDownloadDropdown && (
                <div className="absolute top-0 right-[calc(100%+10px)] z-20 w-[165px] rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_10px_24px_rgba(2,6,23,0.08)]">
                  <p className="mb-2 px-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                    Choose format
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      downloadStudentsCsv();
                      setShowDownloadDropdown(false);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-slate-700 transition-colors hover:bg-slate-100"
                  >
                    <span className="rounded-lg bg-slate-100 p-1.5 text-slate-600">
                      <MdTableChart size={17} />
                    </span>
                    <span className="text-sm font-medium">CSV</span>
                  </button>
                </div>
              )}
            </div>

            <Button
              onClick={toggleAddStudentModal}
              className="bg-primary text-white p-0"
            >
              <BiImport className="mr-1 mt-1" /> Add Student
            </Button>
          </div>
        </div>

        <Table className="min-w-full bg-white border border-gray-300">
          <Table.Head>
            <Table.HeadCell>
              <input
                type="checkbox"
                onChange={(e) =>
                  setSelectedStudentIds(
                    e.target.checked ? students.map((s) => s.id) : []
                  )
                }
                checked={
                  students.length > 0 &&
                  selectedStudentIds.length === students.length
                }
              />
            </Table.HeadCell>

            <Table.HeadCell>Name</Table.HeadCell>
            <Table.HeadCell>Email</Table.HeadCell>
            <Table.HeadCell>Department</Table.HeadCell>
            <Table.HeadCell>Phone Number</Table.HeadCell>
            <Table.HeadCell>Module</Table.HeadCell>
            <Table.HeadCell>Course</Table.HeadCell>
            <Table.HeadCell>Assignments</Table.HeadCell>
            <Table.HeadCell>Actions</Table.HeadCell>
          </Table.Head>

          <Table.Body>
            {students.length > 0 ? (
              students.map((student) => (
                <Table.Row key={student.id} className="cursor-pointer">
                  <Table.Cell>
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.includes(student.id)}
                      onChange={() => handleSelectStudent(student.id)}
                    />
                  </Table.Cell>

                  <Table.Cell>{student.name}</Table.Cell>
                  <Table.Cell>{student.email}</Table.Cell>
                  <Table.Cell>{student.department ?? "-"}</Table.Cell>
                  <Table.Cell>{student.phno ?? "-"}</Table.Cell>

                  <Table.Cell>
                    <button
                      className="text-blue-600 cursor-pointer"
                      onClick={() =>
                        navigate(`/module-for-student`, {
                          state: {
                            studentId: student.id,
                            email: student.email,
                            campusName,
                            clusterId,
                          },
                        })
                      }
                    >
                      View
                    </button>
                  </Table.Cell>

                  <Table.Cell>
                    <button
                      className="text-blue-600 cursor-pointer"
                      onClick={() =>
                        navigate(`/course-for-student`, {
                          state: {
                            studentId: student.id,
                            email: student.email,
                            campusName,
                            clusterId,
                          },
                        })
                      }
                    >
                      View
                    </button>
                  </Table.Cell>

                  <Table.Cell>
                    <button
                      className="text-blue-600 cursor-pointer"
                      onClick={() =>
                        navigate(`/assignments-for-student`, {
                          state: {
                            studentId: student.id,
                            email: student.email,
                            campusName,
                          },
                        })
                      }
                    >
                      View
                    </button>
                  </Table.Cell>

                  {/* ✅ TEXT SHOWS ONLY ON HOVER */}
                  <Table.Cell>
  <div
    onClick={() => handleDeleteStudents([student.id])}
    className="relative group inline-flex items-center cursor-pointer text-grey-600"
  >
    <MdRemoveCircleOutline className="h-6 w-6" />

    {/* ✅ Tooltip like in your image */}
    <div
      className="
        absolute
        bottom-full
        left-1/2
        -translate-x-1/2
        mb-2
        hidden
        group-hover:block
        bg-white
        text-gray-800
        text-xs
        px-3
        py-1.5
        rounded-md
        shadow-lg
        border
        whitespace-nowrap
        z-50
      "
    >
      Remove from Cluster
    </div>
  </div>
</Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell
                  colSpan={9}
                  className="text-center text-gray-500 py-4"
                >
                  No students found
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />

        {selectedStudentIds.length > 0 && (
          <Button
            className="mt-4 bg-red-500 text-white"
            onClick={handleDeleteStudents}
          >
            Remove All
          </Button>
        )}
      </div>

      {/* Modal */}
      <Modal show={showAddStudentModal} onClose={toggleAddStudentModal}>
        <Modal.Header>Add Students</Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddStudents}>
            <div className="mb-4 h-[200px]">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Select Students
              </label>

              <AsyncPaginate
                loadOptions={loadOptions}
                additional={{ page: 1 }}
                isMulti
                value={selectedStudents}
                onChange={setSelectedStudents}
                placeholder="Select students"
                debounceTimeout={400}
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="bg-primary text-white p-0">
                Add
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default StudentsTab;
